import React, { useState } from 'react';
import { TudorChessboard } from '../components/TudorChessboard';
import { BattleAftermath } from '../components/BattleAftermath';
import { Chess } from 'chess.js';
import { Inventory, War, BattleSettings, BattleMove, BattleRecord } from '../types';
import { SupabaseService } from '../services/supabaseService';
import { useNavigate, useParams } from 'react-router-dom';
import { Flag, Clock, Users, Swords } from 'lucide-react';
import { calculateEloChange, BattleResultType } from '../utils/eloLogic';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { AntiCheatService } from '../services/AntiCheatService';


type Side = 'white' | 'black';
type TimeControl = 'unlimited' | '10+0' | '5+0' | '3+0';

interface DeploymentPhaseProps {
  inventory: Inventory;
  onDeploy: (fen: string) => void;
  settings: BattleSettings;
}

interface ActiveBattleProps {
  initialFen: string;
  initialPgn?: string;
  initialHistory?: BattleMove[];
  onGameOver: (result: { winner: 'w' | 'b' | 'draw', casualties: Inventory, enemy_casualties: Inventory, gains?: Inventory, pgn: string }) => void;
  settings: BattleSettings;
  mode?: 'pve' | 'pvp';
  battleId?: string;
  warInventory: Inventory; // Added: Total drafted inventory for reinforcement checks
}

// Map inventory keys to FEN characters
const PIECE_MAP: Record<string, string> = {
  p: 'P', n: 'N', b: 'B', r: 'R', q: 'Q', k: 'K'
};

const PIECE_ICONS: Record<string, string> = {
  P: '♟', N: '♞', B: '♝', R: '♜', Q: '♛', K: '♚',
  p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚'
};

// Helper to construct FEN (Hoisted from DeploymentPhase)
const constructFen = (inventory: Inventory, blockedSquares: number[], side: Side): string => {
  const row1Types = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'] as const;
  let tempInv = { ...inventory };

  const use = (type: keyof Inventory, index: number): string => {
    if (blockedSquares.includes(index)) return '1';
    if (tempInv[type] > 0) {
      tempInv[type]--;
      return PIECE_MAP[type];
    }
    return '1';
  };

  const collapse = (s: string) => {
    let res = "";
    let count = 0;
    for (const char of s) {
      if (char === '1') count++;
      else {
        if (count > 0) { res += count; count = 0; }
        res += char;
      }
    }
    if (count > 0) res += count;
    return res;
  };

  if (side === 'white') {
    const row1Str = row1Types.map((type, i) => use(type, i).toUpperCase()).join("");
    let row2Str = "";
    for (let i = 0; i < 8; i++) { row2Str += use('p', 8 + i).toUpperCase(); }
    return `rnbqkbnr/pppppppp/8/8/8/8/${collapse(row2Str)}/${collapse(row1Str)} w KQkq - 0 1`;
  } else {
    // Black logic
    const row8Str = row1Types.map((type, i) => use(type, i).toLowerCase()).join("");
    let row7Str = "";
    for (let i = 0; i < 8; i++) { row7Str += use('p', 8 + i).toLowerCase(); }
    return `${collapse(row8Str)}/${collapse(row7Str)}/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1`;
  }
};

const constructBattleFen = (
  hostInventory: Inventory,
  guestInventory: Inventory,
  hostBlockedSquares: number[],
  guestBlockedSquares: number[],
  hostSide: Side
): string => {
  const row1Types = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'] as const;

  const collapse = (s: string) => {
    let res = "";
    let count = 0;
    for (const char of s) {
      if (char === '1') count++;
      else {
        if (count > 0) { res += count; count = 0; }
        res += char;
      }
    }
    if (count > 0) res += count;
    return res;
  };

  const buildSideRows = (inventory: Inventory, blockedSquares: number[], pieceCase: 'upper' | 'lower') => {
    const tempInv = { ...inventory };
    const use = (type: keyof Inventory, index: number): string => {
      if (blockedSquares.includes(index)) return '1';
      if (tempInv[type] > 0) {
        tempInv[type]--;
        return PIECE_MAP[type];
      }
      return '1';
    };

    const row1Str = row1Types.map((type, i) => use(type, i)).join("");
    let row2Str = "";
    for (let i = 0; i < 8; i++) { row2Str += use('p', 8 + i); }

    const row1 = pieceCase === 'upper' ? row1Str.toUpperCase() : row1Str.toLowerCase();
    const row2 = pieceCase === 'upper' ? row2Str.toUpperCase() : row2Str.toLowerCase();

    return { row1: collapse(row1), row2: collapse(row2) };
  };

  if (hostSide === 'white') {
    const hostRows = buildSideRows(hostInventory, hostBlockedSquares, 'upper');
    const guestRows = buildSideRows(guestInventory, guestBlockedSquares, 'lower');
    return `${guestRows.row1}/${guestRows.row2}/8/8/8/8/${hostRows.row2}/${hostRows.row1} w KQkq - 0 1`;
  }

  const hostRows = buildSideRows(hostInventory, hostBlockedSquares, 'lower');
  const guestRows = buildSideRows(guestInventory, guestBlockedSquares, 'upper');
  return `${hostRows.row1}/${hostRows.row2}/8/8/8/8/${guestRows.row2}/${guestRows.row1} w KQkq - 0 1`;
};

const FormationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  inventory: Inventory;
  side: Side;
  blockedSquares: number[];
  setBlockedSquares: (squares: number[]) => void;
}> = ({ isOpen, onClose, inventory, side, blockedSquares, setBlockedSquares }) => {
  if (!isOpen) return null;

  // Define the standard slots for the two rows
  const row1Pieces = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'] as const;
  // Row 1 indices: 0-7. Row 2 indices: 8-15 (Pawns)

  // Helper to simulate placement based on current blocks
  const getProjectedPiece = (index: number) => {
    // If blocked, empty
    if (blockedSquares.includes(index)) return null;

    // Determine required type
    let requiredType: keyof Inventory = 'p';
    if (index < 8) requiredType = row1Pieces[index];

    // Count how many of this type were used in *previous* unblocked slots
    let usedCount = 0;
    for (let i = 0; i < index; i++) {
      if (blockedSquares.includes(i)) continue;
      const typeAtIndex = i < 8 ? row1Pieces[i] : 'p';
      if (typeAtIndex === requiredType) usedCount++;
    }

    // Check availability
    if (inventory[requiredType] > usedCount) return PIECE_MAP[requiredType];
    return null;
  };

  const toggleBlock = (index: number) => {
    // Cannot block King (Index 4) or Pawns (Indices 8-15)
    if (index === 4 || index >= 8) return;

    if (blockedSquares.includes(index)) {
      setBlockedSquares(blockedSquares.filter(i => i !== index));
    } else {
      setBlockedSquares([...blockedSquares, index]);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-tudor-black border-2 border-tudor-gold p-6 max-w-2xl w-full shadow-[0_0_50px_rgba(197,160,89,0.2)]">
        <h2 className="text-2xl text-tudor-gold font-serif mb-2 text-center uppercase tracking-widest">Tactical Formation</h2>
        <p className="text-gray-400 text-center text-sm mb-6">Select <span className="text-white font-bold">Elite Pieces</span> to exclude them from battle. Mandates: King & Pawns must deploy.</p>

        <div className="flex flex-col gap-2 bg-[#1a1a1a] p-4 border border-tudor-gray/30 rounded mx-auto w-fit">

          {/* Helper to reverse visual order for Black so it matches board perspective (h..a) */}
          {/* White: a..h (0..7). Black: h..a (7..0) */}

          {/* Rank 2 (Pawns - Indices 8-15) - LOCKED */}
          <div className="flex gap-1">
            {Array.from({ length: 8 }).map((_, i) => {
              // If Black, we render 7..0 order for indices (relative to the rank start)
              const offset = side === 'black' ? (7 - i) : i;
              const idx = 8 + offset; // Rank 2 starts at 8

              const isBlocked = blockedSquares.includes(idx);
              const pieceChar = isBlocked ? 'P' : getProjectedPiece(idx);

              return (
                <button
                  key={idx}
                  onClick={() => toggleBlock(idx)}
                  disabled={true}
                  className={`
                                w-12 h-12 flex items-center justify-center border transition-all cursor-not-allowed
                                bg-tudor-gray/5 border-tudor-gold/10 text-gray-500
                            `}
                  title="Pawns cannot be disabled"
                >
                  <span className="text-3xl font-bold opacity-80">{PIECE_ICONS['p']}</span>
                </button>
              )
            })}
          </div>

          {/* Rank 1 (Elite - Indices 0-7) */}
          <div className="flex gap-1">
            {Array.from({ length: 8 }).map((_, i) => {
              const offset = side === 'black' ? (7 - i) : i;
              const idx = 0 + offset;

              const isBlocked = blockedSquares.includes(idx);
              const isKing = idx === 4;

              // Determine what *should* be there if blocked/missing
              const baseType = row1Pieces[idx];
              const baseChar = PIECE_MAP[baseType];

              const actualPiece = getProjectedPiece(idx);
              const isMissing = !isBlocked && !actualPiece;

              // If blocked -> show what we blocked (baseChar).
              // If missing -> show nothing (null) so it renders as empty slot
              // If present -> show actualPiece (which is baseChar).
              const displayChar = isBlocked ? baseChar : actualPiece;

              return (
                <button
                  key={idx}
                  onClick={() => toggleBlock(idx)}
                  disabled={isKing}
                  className={`
                                w-12 h-12 flex items-center justify-center border transition-all relative
                                ${isKing ? 'border-tudor-gold bg-tudor-gold/10' : ''}
                                ${isBlocked
                      ? 'bg-red-900/10 border-red-900/30 text-red-800' // Red "Ghost" style (User Disabled)
                      : isMissing
                        ? 'bg-transparent border-dashed border-zinc-800 text-zinc-800' // Missing "Ghost" style (Inventory Empty)
                        : isKing ? 'text-tudor-gold' : 'bg-tudor-gray/10 border-tudor-gold/30 hover:bg-tudor-gold/20 text-gray-300'}
                            `}
                >
                  <span className={`text-3xl ${isBlocked ? 'opacity-40 grayscale' : ''} ${isMissing ? 'opacity-10' : ''}`}>
                    {displayChar ? PIECE_ICONS[displayChar] : ""}
                  </span>

                  {isBlocked && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-12 h-[1px] bg-red-600 rotate-45 absolute"></div>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full bg-tudor-gold text-tudor-black font-bold py-3 mt-6 hover:bg-yellow-600 transition-colors uppercase tracking-widest"
        >
          CONFIRM FORMATION
        </button>
      </div>
    </div >
  );
};

export const DeploymentPhase: React.FC<DeploymentPhaseProps> = ({ inventory, onDeploy, settings }) => {
  // Indices 0-7 (Rank 1/8), 8-15 (Rank 2/7)
  const [blockedSquares, setBlockedSquares] = useState<number[]>([]);
  const [isFormationOpen, setIsFormationOpen] = useState(false);

  const constructStartingFen = () => {
    let tempInv = { ...inventory };

    // Helper to check and decrement
    // UPDATED: Now respects blocked status
    // If blocked, returns '1' and DOES NOT consume
    const use = (type: 'p' | 'n' | 'b' | 'r' | 'q' | 'k', index: number): string => {
      if (blockedSquares.includes(index)) return '1';

      if (tempInv[type] > 0) {
        tempInv[type]--;
        return PIECE_MAP[type];
      }
      return '1'; // 1 empty square
    };

    // Row 1 (Pieces): Indices 0-7
    const row1Types = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'] as const;

    // Row 2 (Pawns): Indices 8-15
    // Type is always 'p'

    const collapse = (s: string) => {
      let res = "";
      let count = 0;
      for (const char of s) {
        if (char === '1') count++;
        else {
          if (count > 0) { res += count; count = 0; }
          res += char;
        }
      }
      if (count > 0) res += count;
      return res;
    };

    if (settings.side === 'white') {
      // White: Rank 1 is Index 0-7. Rank 2 is 8-15.
      // FEN: Black, Black, ..., Rank 2, Rank 1.

      const row1Str = row1Types.map((type, i) => use(type, i).toUpperCase()).join("");
      let row2Str = "";
      for (let i = 0; i < 8; i++) { row2Str += use('p', 8 + i).toUpperCase(); }

      const enemyRank8 = "rnbqkbnr";
      const enemyRank7 = "pppppppp";

      return `${enemyRank8}/${enemyRank7}/8/8/8/8/${collapse(row2Str)}/${collapse(row1Str)} w KQkq - 0 1`;

    } else {
      // Black: Rank 8 is Index 0-7 (as they are the 'back' row). Rank 7 is 8-15.
      // FEN starts with Rank 8.

      const row8Str = row1Types.map((type, i) => use(type, i).toLowerCase()).join("");
      let row7Str = "";
      for (let i = 0; i < 8; i++) { row7Str += use('p', 8 + i).toLowerCase(); }

      const enemyRank1 = "RNBQKBNR";
      const enemyRank2 = "PPPPPPPP";

      return `${collapse(row8Str)}/${collapse(row7Str)}/8/8/8/8/${enemyRank2}/${enemyRank1} w KQkq - 0 1`;
    }
  };

  const [fen, setFen] = useState(constructStartingFen());
  const navigate = useNavigate();

  // Recalculate FEN when settings OR blockedSquares change
  React.useEffect(() => {
    const newFen = constructStartingFen();
    console.log(`[Deployment] FEN update: ${newFen}`);
    setFen(newFen);
  }, [settings.side, inventory, blockedSquares]);

  const isKingPresent = inventory.k > 0;

  return (
    <div className="flex flex-col items-center">
      <FormationModal
        isOpen={isFormationOpen}
        onClose={() => setIsFormationOpen(false)}
        inventory={inventory}
        side={settings.side}
        blockedSquares={blockedSquares}
        setBlockedSquares={setBlockedSquares}
      />

      <div className="mb-4 text-center">
        <h3 className="text-xl text-tudor-gold">DEPLOYMENT PHASE</h3>
        <p className="text-sm text-gray-400">Review your formation.</p>
        <button
          onClick={() => setIsFormationOpen(true)}
          className="mt-2 text-xs border border-tudor-gold/50 text-tudor-gold px-2 py-1 hover:bg-tudor-gold/10"
        >
          MODIFY FORMATION
        </button>
        {/* <p className="text-[10px] text-gray-600 font-mono mt-2 break-all max-w-[300px]">{fen}</p> */}
      </div>
      <div className="w-full h-[600px] max-w-[600px]">
        <TudorChessboard position={fen} arePiecesDraggable={false} boardOrientation={settings.side} />
      </div>

      {!isKingPresent ? (
        <div className="mt-6 p-4 bg-red-900/20 border border-red-500 rounded text-center max-w-sm">
          <h4 className="text-red-500 font-bold mb-2 uppercase tracking-widest">MONARCH MISSING</h4>
          <p className="text-sm text-gray-400 mb-4">You cannot take the field without a King. You must elect a successor.</p>
          <button
            onClick={() => navigate('/war-room')}
            className="bg-red-800 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition-colors uppercase text-sm"
          >
            Go to Royal Commission
          </button>
        </div>
      ) : (
        <button
          onClick={() => onDeploy(fen)}
          className="mt-6 bg-tudor-gold text-tudor-black font-bold py-2 px-8 rounded hover:bg-yellow-600 shadow-[0_0_15px_rgba(197,160,89,0.3)] animate-pulse"
        >
          CONFIRM & FIGHT
        </button>
      )}
    </div>
  );
};



interface ActiveBattleProps {
  initialFen: string;
  onGameOver: (result: { winner: 'w' | 'b' | 'draw', casualties: Inventory, enemy_casualties: Inventory, gains?: Inventory, pgn: string }) => void;
  settings: BattleSettings;
  mode?: 'pve' | 'pvp';
  battleId?: string;
  warInventory: Inventory; // Added: Total drafted inventory for reinforcement checks
}

const ChessTimer: React.FC<{ initialTime: number, isRunning: boolean, onTimeout: () => void }> = ({ initialTime, isRunning, onTimeout }) => {
  const [time, setTime] = useState(initialTime);

  React.useEffect(() => {
    if (!isRunning || time <= 0) return;
    const timer = setInterval(() => {
      setTime(t => {
        if (t <= 1) {
          clearInterval(timer);
          onTimeout();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isRunning, time, onTimeout]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`px-4 py-2 font-mono text-xl border rounded ${isRunning ? 'border-tudor-gold text-tudor-gold bg-tudor-gold/10' : 'border-tudor-gray text-gray-500'}`}>
      <div className="flex items-center gap-2">
        <Clock size={16} />
        {formatTime(time)}
      </div>
    </div>
  );
};

const PromotionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSelect: (piece: 'q' | 'r' | 'b' | 'n') => void;
  availableOptions: ('q' | 'r' | 'b' | 'n')[];
}> = ({ isOpen, onClose, onSelect, availableOptions }) => {
  if (!isOpen) return null;

  const options = [
    { type: 'q', label: 'Queen', icon: PIECE_ICONS['q'] },
    { type: 'r', label: 'Rook', icon: PIECE_ICONS['r'] },
    { type: 'b', label: 'Bishop', icon: PIECE_ICONS['b'] },
    { type: 'n', label: 'Knight', icon: PIECE_ICONS['n'] },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100]">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-tudor-black border-2 border-tudor-gold p-6 max-w-lg w-full shadow-[0_0_50px_rgba(197,160,89,0.2)] animate-fade-in-up">
        <h2 className="text-2xl text-tudor-gold font-serif mb-2 text-center uppercase tracking-widest">Field Promotion</h2>
        <p className="text-gray-400 text-center text-xs mb-6 uppercase tracking-wider">Reinforcements Available</p>

        <div className="flex gap-4 justify-center">
          {options.map((opt) => {
            const isAvailable = availableOptions.includes(opt.type as any);
            return (
              <button
                key={opt.type}
                onClick={() => isAvailable && onSelect(opt.type as any)}
                disabled={!isAvailable}
                className={`
                            w-20 h-20 flex flex-col items-center justify-center border transition-all rounded-lg
                            ${isAvailable
                    ? 'bg-tudor-gold/10 border-tudor-gold hover:bg-tudor-gold hover:text-black text-tudor-gold cursor-pointer transform hover:scale-105 shadow-[0_0_15px_rgba(197,160,89,0.2)]'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-700 cursor-not-allowed opacity-40 grayscale'}
                        `}
                title={!isAvailable ? "Unit capacity reached" : `Deploy ${opt.label}`}
              >
                <span className="text-4xl leading-none mb-1">{opt.icon}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-6 text-center">
          <button onClick={onClose} className="text-xs text-red-500 hover:text-red-400 uppercase tracking-widest hover:underline decoration-dotted underline-offset-4">Cancel Manoeuvre</button>
        </div>
      </div>
    </div>
  );
};

const ActiveBattle: React.FC<ActiveBattleProps> = ({ initialFen, initialPgn, initialHistory, onGameOver, settings, mode = 'pve', battleId, warInventory }) => {
  // Use Ref to maintain game state (History/PGN) without resetting on renders
  const gameRef = React.useRef((() => {
    if (initialPgn) {
      try {
        const g = new Chess();
        g.loadPgn(initialPgn);
        return g;
      } catch (e) { console.error("ActiveBattle PGN Load Failed", e); }
    }
    return new Chess(initialFen);
  })());
  const game = gameRef.current;

  const [fen, setFen] = useState(initialFen);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [promotions, setPromotions] = useState<Inventory>({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 });
  const [history, setHistory] = useState<BattleMove[]>(initialHistory || []);

  const createMoveRecord = (gameInstance: Chess, move: any): BattleMove => ({
    from: move.from,
    to: move.to,
    fen: gameInstance.fen(),
    san: move.san,
    timestamp: Date.now(),
    promotion: move.promotion
  });

  // Promotion Handling
  const [promotionPending, setPromotionPending] = useState<{ from: string, to: string } | null>(null);
  const [promotionOptions, setPromotionOptions] = useState<('q' | 'r' | 'b' | 'n')[]>([]);

  // Realtime Sync Logic
  React.useEffect(() => {
    if (!battleId || mode !== 'pvp') return;

    // console.log(`[ActiveBattle] Subscribing to battle ${battleId}`);
    const channel = supabase.channel(`battle_sync_${battleId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'battles', filter: `id=eq.${battleId}` },
        (payload: { new: { pgn: any; }; }) => {
          const newPgn = payload.new.pgn;
          const currentPgn = game.pgn();

          if (newPgn && newPgn !== currentPgn) {
            console.log(`[ActiveBattle] Remote move received.`);
            try {
              game.loadPgn(newPgn);
              setFen(game.fen()); // Update Board
              if (game.isGameOver()) handleGameOver(game);
            } catch (e) {
              console.error("Failed to sync PGN", e);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [battleId, mode]);

  // Calculate available promotions based on "Regain Only" rules (Std Chess Limits)
  const getPieceCounts = (gameInstance: Chess, side: 'w' | 'b') => {
    const counts = { q: 0, r: 0, b: 0, n: 0 };
    const board = gameInstance.board();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = board[r][c];
        if (p && p.color === side) {
          if (p.type in counts) counts[p.type as keyof typeof counts]++;
        }
      }
    }
    return counts;
  };

  const handlePromotionSelect = (type: 'q' | 'r' | 'b' | 'n') => {
    if (!promotionPending) return;

    try {
      const moveResult = game.move({
        from: promotionPending.from,
        to: promotionPending.to,
        promotion: type
      });

      if (moveResult) {
        setPromotions(prev => ({
          ...prev,
          [type]: prev[type as keyof Inventory] + 1
        }));
        setFen(game.fen());

        const moveRecord = createMoveRecord(game, moveResult);
        const newHistory = [...history, moveRecord];
        setHistory(newHistory);

        if (battleId) {
          SupabaseService.sendBattleMove(battleId, game.pgn(), newHistory);
        }

        if (game.isGameOver()) handleGameOver(game);
      }
    } catch (e) { console.error("Promotion Error", e); }

    setPromotionPending(null);
  };

  // Timers (in seconds)
  const getInitialTime = () => {
    switch (settings.timeControl) {
      case '10+0': return 10 * 60;
      case '5+0': return 5 * 60;
      case '3+0': return 3 * 60;
      default: return Infinity;
    }
  };
  const isTimed = settings.timeControl !== 'unlimited';

  // Auto-scroll move list
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [fen]); // Scroll on new move (fen change)

  const handleGameOver = (finalGame: Chess = game, isResignation = false) => {
    let winner: 'w' | 'b' | 'draw' = 'draw';
    if (!isResignation) {
      if (finalGame.isCheckmate()) {
        winner = finalGame.turn() === 'w' ? 'b' : 'w'; // Turn is loser's turn
      } else if (finalGame.isDraw() || finalGame.isStalemate() || finalGame.isThreefoldRepetition()) {
        winner = 'draw';
      }
    } else {
      // If resigned, the OTHER side wins. 
    }

    const casualties: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    const enemy_casualties: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    const history = finalGame.history({ verbose: true }); // Now usually works because game instance is persistent

    history.forEach(move => {
      if (move.captured) {
        const type = move.captured as keyof Inventory;
        const isPlayerMove = (settings.side === 'white' && move.color === 'w') || (settings.side === 'black' && move.color === 'b');

        if (isPlayerMove) {
          enemy_casualties[type]++;
        } else {
          casualties[type]++;
        }
      }
    });

    onGameOver({ winner, casualties, enemy_casualties, gains: promotions, pgn: finalGame.pgn() });
  };

  const makeRandomMove = () => {
    if (game.isGameOver()) return;
    const moves = game.moves();
    if (moves.length > 0) {
      const randomMove = moves[Math.floor(Math.random() * moves.length)];
      const moveResult = game.move(randomMove); // Mutate Ref
      setFen(game.fen());

      if (moveResult) {
        const moveRecord = createMoveRecord(game, moveResult);
        const newHistory = [...history, moveRecord];
        setHistory(newHistory);

        if (battleId) {
          SupabaseService.sendBattleMove(battleId, game.pgn(), newHistory);
        }
      }

      if (game.isGameOver()) handleGameOver(game);
    }
  };

  // AI Move Effect
  React.useEffect(() => {
    if (mode === 'pvp') return;
    const isAiTurn = (game.turn() === 'w' && settings.side === 'black') || (game.turn() === 'b' && settings.side === 'white');

    if (isAiTurn && !game.isGameOver()) {
      const timeout = setTimeout(() => {
        makeRandomMove();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [fen, settings.side, mode]); // Depend on fen to re-evaluate turn

  /* Updated onDrop with Promotion Modal Logic */
  const onDrop = (sourceSquare: string, targetSquare: string, pieceString: string) => {
    try {
      // 1. Turn Guard
      const isMyTurn = (game.turn() === 'w' && settings.side === 'white') || (game.turn() === 'b' && settings.side === 'black');
      if (!isMyTurn) return false;



      // 2. Promotion Check (Manual simplified check first)
      const internalPiece = game.get(sourceSquare as any);
      if (!internalPiece) return false;

      const pieceType = internalPiece.type; // 'p'
      const pieceColor = internalPiece.color; // 'w' or 'b'
      const targetRank = targetSquare[1];
      const isPromotionRank = (pieceColor === 'w' && targetRank === '8') || (pieceColor === 'b' && targetRank === '1');
      const isPawn = pieceType === 'p';

      if (isPawn && isPromotionRank) {
        // Verify if it's a valid move using temp game
        const tempGame = new Chess(game.fen());
        const moveAttempt = tempGame.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: 'q', // Try with queen to validate move geometry
        });

        if (!moveAttempt) return false; // Invalid move (e.g. trying to push blocked pawn)

        // Calculate Allowed Promotions
        const counts = getPieceCounts(game, game.turn());
        const allowed: ('q' | 'r' | 'b' | 'n')[] = ['r', 'b', 'n'];
        if (counts.q < 1) allowed.push('q');

        setPromotionOptions(allowed);
        setPromotionPending({ from: sourceSquare, to: targetSquare });
        return false; // Snap back piece, wait for modal selection
      }

      // 3. Normal Move execution
      const moveResult = game.move({
        from: sourceSquare,
        to: targetSquare
      });

      if (!moveResult) return false;

      // 4. Update State
      setFen(game.fen());

      const moveRecord = createMoveRecord(game, moveResult);
      const newHistory = [...history, moveRecord];
      setHistory(newHistory);

      if (battleId) {
        SupabaseService.sendBattleMove(battleId, game.pgn(), newHistory);
      }

      // 5. Game Over Check
      if (game.isGameOver()) {
        handleGameOver(game);
      }

      return true;
    } catch (e) {
      console.error("Move execution error", e);
      return false;
    }
  };

  const handleResign = () => {
    if (!window.confirm("Are you sure you want to resign? (Mercy Rule: Opponent pieces will be restored)")) return;

    const casualties: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
    // MERCY RULE: Enemy casualties = 0
    const enemy_casualties: Inventory = { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

    const history = game.history({ verbose: true });
    history.forEach(move => {
      if (move.captured) {
        const type = move.captured as keyof Inventory;
        // Only count OUR losses. Enemy losses are ignored/restored.
        const isPlayerMove = (settings.side === 'white' && move.color === 'w') || (settings.side === 'black' && move.color === 'b');
        if (!isPlayerMove && move.color !== (settings.side === 'white' ? 'w' : 'b')) {
          // Ops, logic check:
          // If I am white, enemy is black. If black captures, I lose a piece.
          // move.color is the mover.
          const moverIsEnemy = (settings.side === 'white' && move.color === 'b') || (settings.side === 'black' && move.color === 'w');
          if (moverIsEnemy) {
            casualties[type]++;
          }
        }
      }
    });

    // Winner is opponent
    onGameOver({
      winner: settings.side === 'white' ? 'b' : 'w',
      casualties,
      enemy_casualties,
      gains: promotions, // We still keep our promotions if we resign?? Usually yes.
      pgn: game.pgn()
    });
  };

  const isUserTurn = (game.turn() === 'w' && settings.side === 'white') || (game.turn() === 'b' && settings.side === 'black');

  // Move History Calculation
  const moveHistory = game.history();
  const movePairs = [];
  for (let i = 0; i < moveHistory.length; i += 2) {
    movePairs.push({
      num: Math.floor(i / 2) + 1,
      white: moveHistory[i],
      black: moveHistory[i + 1] || ''
    });
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen w-screen bg-black overflow-hidden font-mono">

      <PromotionModal
        isOpen={!!promotionPending}
        onClose={() => setPromotionPending(null)}
        onSelect={handlePromotionSelect}
        availableOptions={promotionOptions}
      />

      {/* LEFT/CENTER: CHESSBOARD ARENA */}
      <div className="flex-grow flex items-center justify-center p-4 lg:p-10 relative bg-zinc-950">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(180,142,61,0.08),_transparent_60%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_40%,rgba(255,255,255,0.03)_50%,transparent_60%)] opacity-30"></div>

        {/* Board Container */}
        <div className="relative z-10 h-full max-h-[85vh] aspect-square shadow-2xl border-[1px] border-zinc-800 rounded-xl bg-black/40 backdrop-blur-sm p-4">
          <TudorChessboard
            position={fen}
            boardOrientation={settings.side}
            arePiecesDraggable={isUserTurn}
            onPieceDrop={onDrop}

            isDraggablePiece={({ piece }: any) => {
              // Only allow local player’s colour
              const color = piece[0]; // 'w' or 'b'
              return settings.side === "white" ? color === "w" : color === "b";
            }}
          />

        </div>
      </div>

      {/* RIGHT: COMMAND PANEL (HUD) */}
      <div className="w-full lg:w-96 bg-zinc-950 border-l border-zinc-800 flex flex-col relative z-20 shadow-2xl">
        {/* Gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(180,142,61,0.1),_transparent_50%)] pointer-events-none"></div>

        {/* Header */}
        <div className="p-6 border-b border-zinc-800 text-center relative z-10">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 mb-2 uppercase">Battlefield Control</div>
          <h2 className="text-2xl text-tudor-gold font-serif tracking-widest">{isUserTurn ? 'Your Orders' : 'Enemy Volley'}</h2>
        </div>

        {/* TURN INDICATOR */}
        <div className={`transition-all duration-500 p-4 flex justify-center items-center shrink-0 border-b border-zinc-800 ${isUserTurn ? 'bg-tudor-gold/5' : 'bg-red-900/5'}`}>
          <div className={`text-3xl ${isUserTurn ? 'text-tudor-gold animate-pulse' : 'text-red-900/50 grayscale'}`}>
            {isUserTurn ? <Swords className="w-8 h-8" /> : <Clock className="w-8 h-8" />}
          </div>
        </div>

        {/* STATS ROW */}
        <div className="grid grid-cols-2 border-b border-zinc-800 relative z-10 bg-black/20">
          {/* OPPONENT */}
          <div className="p-4 border-r border-zinc-800">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-zinc-600"></div> ENEMY
            </div>
            {isTimed && <ChessTimer initialTime={getInitialTime()} isRunning={!isUserTurn && !game.isGameOver()} onTimeout={() => handleResign()} />}
          </div>
          {/* USER */}
          <div className="p-4">
            <div className="text-[10px] text-tudor-gold uppercase tracking-wider font-bold mb-1 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-tudor-gold animate-pulse"></div> COMMANDER
            </div>
            {isTimed && <ChessTimer initialTime={getInitialTime()} isRunning={isUserTurn && !game.isGameOver()} onTimeout={() => handleResign()} />}
          </div>
        </div>

        {/* MOVE HISTORY (Replacing Space) */}
        <div className="flex-grow flex flex-col min-h-0 relative z-10 bg-black/10">
          <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600 border-b border-zinc-800/50 bg-black/20">
            Combat Log
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent">
            {movePairs.length === 0 ? (
              <div className="text-zinc-700 text-xs italic text-center mt-10">No moves recorded.</div>
            ) : (
              movePairs.map((pair) => (
                <div key={pair.num} className="grid grid-cols-[30px_1fr_1fr] text-sm items-center hover:bg-white/5 rounded px-2 py-1 transition-colors">
                  <span className="text-zinc-600 font-mono text-xs">{pair.num}.</span>
                  <span className={`font-mono ${pair.num === movePairs.length && !pair.black ? 'text-tudor-gold font-bold' : 'text-zinc-300'}`}>{pair.white}</span>
                  <span className={`font-mono ${pair.num === movePairs.length && pair.black ? 'text-tudor-gold font-bold' : 'text-zinc-300'}`}>{pair.black}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ACTIONS */}
        <div className="p-6 border-t border-zinc-800 bg-zinc-900/50 relative z-10">
          <button
            onClick={handleResign}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-950/10 border border-red-900/30 text-red-500 hover:bg-red-950/30 hover:border-red-800 hover:text-red-400 transition-all font-bold uppercase tracking-[0.2em] text-xs group rounded-lg"
          >
            <Flag size={14} className="group-hover:rotate-12 transition-transform" /> Surrender Field
          </button>
        </div>
      </div>

    </div>
  );
};

const BattleSetupModal: React.FC<{ onConfirm: (s: BattleSettings) => void }> = ({ onConfirm }) => {
  const [side, setSide] = useState<Side>('white');
  const [time, setTime] = useState<TimeControl>('unlimited');

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      <div className="relative bg-tudor-black border-2 border-tudor-gold p-8 max-w-md w-full shadow-[0_0_50px_rgba(197,160,89,0.2)]">
        <h2 className="text-2xl text-tudor-gold font-serif mb-6 text-center uppercase tracking-widest">Battle Settings</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-gray-400 text-sm mb-2 uppercase tracking-wide">Choose Side</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSide('white')}
                className={`p-4 border ${side === 'white' ? 'border-tudor-gold bg-tudor-gold/20 text-white' : 'border-gray-700 text-gray-500'} transition-all`}
              >
                WHITE
              </button>
              <button
                onClick={() => setSide('black')}
                className={`p-4 border ${side === 'black' ? 'border-tudor-gold bg-tudor-gold/20 text-white' : 'border-gray-700 text-gray-500'} transition-all`}
              >
                BLACK
              </button>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2 uppercase tracking-wide">Time Control</label>
            <select
              value={time}
              onChange={(e) => setTime(e.target.value as TimeControl)}
              className="w-full bg-black border border-tudor-gray p-3 text-tudor-text focus:border-tudor-gold outline-none"
            >
              <option value="unlimited">Unlimited</option>
              <option value="10+0">Rapid (10 min)</option>
              <option value="5+0">Blitz (5 min)</option>
              <option value="3+0">Blitz (3 min)</option>
            </select>
          </div>

          <button
            onClick={() => onConfirm({ side, timeControl: time })}
            className="w-full bg-tudor-gold text-tudor-black font-bold py-3 mt-4 hover:bg-yellow-600 transition-colors uppercase tracking-widest"
          >
            CONFIRM ORDERS
          </button>
        </div>
      </div>
    </div>
  );
};

const Battle: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth(); // 1. Get User
  // We use the same route parameter ':warId' from App.tsx but interpret it as potentially a Battle ID now
  // To avoid changing App.tsx repeatedly, let's treat the param 'warId' as 'id' which could be either.
  // Actually, WarRoom passes a Battle ID now.
  const { warId } = useParams();
  const battleId = warId; // Alias for clarity context

  const [stage, setStage] = useState<'DEPLOY' | 'FIGHT' | 'REPORT'>('DEPLOY');
  const [showSettings, setShowSettings] = useState(false);
  const [war, setWar] = useState<War | null>(null);
  const [battleRecord, setBattleRecord] = useState<BattleRecord | null>(null); // BattleRecord
  const [fen, setFen] = useState('');
  const [battleResult, setBattleResult] = useState<{ winner: string, casualties: Inventory, enemy_casualties: Inventory } | null>(null);
  const [settings, setSettings] = useState<BattleSettings>({ side: 'white', timeControl: 'unlimited' });
  const [inventory, setInventory] = useState<Inventory>({ p: 0, n: 0, b: 0, r: 0, q: 0, k: 1 });



  React.useEffect(() => {
    const init = async () => {
      // 1. Try to load as War Campaign
      let warData = warId ? await SupabaseService.getWarById(warId) : null;
      let activeBattleData = null;

      // 2. If not found, it might be a Quick Play Battle ID directly or a Campaign Battle ID
      if (!warData && warId) {
        const battle = await SupabaseService.getBattleById(warId);
        if (battle) {
          // Case A: Quick Play (No War ID)
          if (!battle.war_id) {
            activeBattleData = battle;

            const s = battle.settings as BattleSettings;
            const amHost = user?.id === battle.player_id;

            const hostIsWhite = s.side === 'white' || !s.side;

            const whiteInv = s.white_inventory || { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };
            const blackInv = s.black_inventory || { p: 0, n: 0, b: 0, r: 0, q: 0, k: 0 };

            let myInv = amHost ? (hostIsWhite ? whiteInv : blackInv) : (hostIsWhite ? blackInv : whiteInv);
            let enemyInv = amHost ? (hostIsWhite ? blackInv : whiteInv) : (hostIsWhite ? whiteInv : blackInv);

            warData = {
              id: 'quickplay',
              player_id: battle.player_id,
              opponent_id: battle.opponent_id,
              status: 'ACTIVE',
              inventory: myInv,
              enemy_inventory: enemyInv,
              settings: s,
              created_at: '',
              battles_fought: 0
            } as War;
          } else {
            // Case B: Campaign Battle (Has War ID)
            activeBattleData = battle;
            warData = await SupabaseService.getWarById(battle.war_id);
          }
        }
      }

      if (!warData) {
        navigate('/');
        return;
      }

      setWar(warData);
      setInventory(warData.inventory || { p: 0, n: 0, b: 0, r: 0, q: 0, k: 1 }); // Fallback

      // 3. Load Battle State
      if (activeBattleData) {
        setBattleRecord(activeBattleData);
        setSettings(activeBattleData.settings as BattleSettings);

        if (activeBattleData.pgn) {
          try {
            const tempGame = new Chess();
            tempGame.loadPgn(activeBattleData.pgn);
            setFen(tempGame.fen());
            setStage('FIGHT');
          } catch (e) { console.error(e); }
        } else {
          // If no PGN, we might be in Fight mode but at start
          // Or we might need to deploy?
          // Quick Play skips deploy.
          // Ensure FEN is set?
          // ActiveBattle will init with initialFen.
          // constructBattleFen needs to be called if no PGN?
          // If PGN is null, `ActiveBattle` inits with `initialFen`.
          // `initialFen` comes from `fen` state.
          // We need to set `fen` using `constructBattleFen` here if strictly needed.
          // But existing code relies on `handleDeploy` setting it, OR `useEffect` below auto-deploying.
          // The Auto-Deploy effect (Line 1155) will trigger purely based on `stage === 'DEPLOY'` (default?)
          // Wait, default `stage` is 'DEPLOY'.
          // So if QP, we let it Auto Deploy.
        }
      } else {
        // Campaign Logic: Find active
        const battles = await SupabaseService.getBattlesByWarId(warData.id === 'quickplay' ? '' : warData.id);
        const active = battles.find(b => b.status === 'active');
        if (active) {
          setBattleRecord(active);
          setSettings(active.settings as BattleSettings);
          if (active.pgn) {
            const tempGame = new Chess();
            tempGame.loadPgn(active.pgn);
            setFen(tempGame.fen());
            setStage('FIGHT');
          }
        } else {
          // No active battle? (Should happen in Campaign Flow if between battles)
          // Default to DEPLOY mode for next battle.
          setShowSettings(true); // Let user pick side/settings? Or inferred?
          // In Campaign, Next Battle logic usually handled in WarRoom. 
          // If we are here, we probably clicked "Fight" and created a battle?
          // If no battle exists, we might default to settings.
        }
      }
    };

    if (user && warId) init();
  }, [warId, user, navigate]);

  // Derived Logic for PvP / Roles
  const amHost = war?.player_id === user?.id;
  // If we don't have opponent_id in war, check if we are NOT the host but viewing a battle
  // But strictly, war.opponent_id should be set.
  const amGuest = war?.opponent_id === user?.id;

  // Color Logic:
  // Settings.side refers to HOST's side.
  // If I am Guest, I play opposite.
  // Ensure we have a valid side default
  let mySide: Side = settings.side || 'white';
  if (amGuest) {
    if (settings.opponent_side) {
      mySide = settings.opponent_side as Side;
    } else {
      const hostSide = settings.side || 'white';
      mySide = hostSide === 'white' ? 'black' : 'white';
    }
  }

  // Inventory Logic:
  // Host uses war.inventory (Player Inventory)
  // Guest uses war.enemy_inventory
  // Default to inventory if spectating or solo
  const myInventory = amGuest ? war?.enemy_inventory : war?.inventory;

  // Mode Logic
  // Explicitly check opponent_type
  // Default to pve if missing (safe fallback) unless we detect two players
  const isPvP = settings.opponent_type === 'player' || (war?.opponent_id && war.player_id);
  const gameMode = isPvP ? 'pvp' : 'pve';

  const effectiveSettings = { ...settings, side: mySide };

  // Auto-Start Check: Skip Deployment using stored DB settings
  React.useEffect(() => {
    // Critical: Do not deploy if User is not yet confirmed (amHost/amGuest invalid)
    if (!war || !user || stage !== 'DEPLOY') return;

    // settings is loaded from the battle record
    // If settings.host_formation or settings.opponent_formation are missing, default to empty (standard formation)
    const hostSide = settings.side || 'white';

    let hostFormation = settings.host_formation || [];
    let opponentFormation = settings.opponent_formation || [];

    // Fallback/Override from white/black specific settings (Quick Play)
    if (settings.white_formation || settings.black_formation) {
      if (hostSide === 'white') {
        hostFormation = settings.white_formation || [];
        opponentFormation = settings.black_formation || [];
      } else {
        hostFormation = settings.black_formation || [];
        opponentFormation = settings.white_formation || [];
      }
    }

    console.log("[Battle] Auto-Deploying...", { hostSize: hostFormation.length, opponentSize: opponentFormation.length, side: mySide, amHost, amGuest, uid: user.id });

    let activeEnemyInventory = war.enemy_inventory;
    const enemyCount = activeEnemyInventory ? Object.values(activeEnemyInventory).reduce((a: number, b: number) => a + b, 0) : 0;
    if (enemyCount === 0 && settings.opponent_type === 'ai') {
        activeEnemyInventory = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };
    }

    const startFen = constructBattleFen(war.inventory, activeEnemyInventory, hostFormation, opponentFormation, hostSide);
    handleDeploy(startFen);

  }, [war, user, stage, myInventory, mySide, settings, amHost, amGuest]);


  const handleDeploy = (startFen: string) => {
    setFen(startFen);
    setStage('FIGHT');
  };


  const handleGameOver = async (result: { winner: 'w' | 'b' | 'draw', casualties: Inventory, enemy_casualties: Inventory, eloChange?: number, gains?: Inventory, pgn: string }) => {

    // 1. Calculate ELO Change
    const profile = await SupabaseService.getProfile();
    const playerRating = profile.rating;

    // Determine detailed outcome
    let battleOutcome: BattleResultType = 'draw';
    let winnerId: 'player' | 'enemy' | 'draw' = 'draw';

    if (result.winner !== 'draw') {
      // Determine if player matches the winning color
      // Note: effectiveSettings.side is MY side.
      // If result.winner == 'w' and mySide == 'white', I won.
      const isPlayerWinner = (result.winner === 'w' && mySide === 'white') || (result.winner === 'b' && mySide === 'black');

      battleOutcome = isPlayerWinner ? 'win' : 'loss';

      // WinnerId for Server: 'player' means HOST WON. 'enemy' means GUEST/AI WON.
      // We need to map "I Won" to "Who am I?"
      if (amHost) {
        winnerId = isPlayerWinner ? 'player' : 'enemy';
      } else if (amGuest) {
        // If I (Guest) won, winnerId is 'enemy' (from Host/Server perspective)
        winnerId = isPlayerWinner ? 'enemy' : 'player';
      } else {
        // AI/Solo
        winnerId = isPlayerWinner ? 'player' : 'enemy';
      }
    }

    // Persist War State (Last Winner for Initiative)
    if (war) {
      await SupabaseService.updateWarSettings(war.id, undefined as any, winnerId);
    }

    // Anti-Cheat Analysis
    if (user && result.pgn) {
      AntiCheatService.analyzeGame(result.pgn, mySide === 'white' ? 'w' : 'b', user.id);
    }

    // ... continue logic ...
    // Elo Logic (Block for PvE)
    let ratingChange = 0;
    let newRating = playerRating;

    if (isPvP) {
      const opponentRating = playerRating + (Math.floor(Math.random() * 200) - 100);
      ratingChange = calculateEloChange(playerRating, opponentRating, battleOutcome);
      newRating = Math.max(0, playerRating + ratingChange);
      console.log(`[ELO] PvP Match vs ${opponentRating}. Change: ${ratingChange}. New: ${newRating}`);
      await SupabaseService.updateRating(newRating);
    } else {
      console.log(`[ELO] PvE Match. No Rating Change.`);
    }

    // 2. set Result with ELO
    const finalResult = { ...result, eloChange: ratingChange };
    setBattleResult(finalResult);
    setStage('REPORT');

    if (war) {
      await SupabaseService.recordBattleResult(
        war.id,
        {
          winner: result.winner as any,
          casualties: result.casualties,
          enemy_casualties: result.enemy_casualties,
          eloChange: ratingChange,
          gains: result.gains,
          pgn: result.pgn
        },
        battleRecord?.id // Pass the specific battle ID if available
      );
    }
  };


  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-tudor-gold animate-pulse">AUTHENTICATING COMMANDER...</div>;

  if (!war) return null;

  return (
    <div className="min-h-screen bg-tudor-black text-tudor-text p-4 flex flex-col items-center justify-center relative overflow-hidden">

      {/* Settings Modal Overlay */}
      {showSettings && (
        <BattleSetupModal onConfirm={(s) => {
          setSettings(s);
          setShowSettings(false);
          if (war) SupabaseService.updateWarSettings(war.id, s);
        }} />
      )}

      {/* Main Content - Blurred if settings open */}
      <div className={`transition-all duration-500 w-full flex flex-col items-center ${showSettings ? 'blur-sm grayscale opacity-50 pointer-events-none' : ''}`}>

        {stage === 'DEPLOY' && myInventory && <DeploymentPhase inventory={myInventory} onDeploy={handleDeploy} settings={effectiveSettings} />}

        {stage === 'FIGHT' && myInventory && war && <ActiveBattle
          initialFen={fen}
          initialPgn={battleRecord?.pgn}
          initialHistory={battleRecord?.battle_history}
          onGameOver={handleGameOver}
          settings={effectiveSettings}
          mode={gameMode}
          battleId={battleRecord?.id}
          warInventory={myInventory}
        />}

        {stage === 'REPORT' && battleResult && (
          <BattleAftermath
            warId={war.id}
            battleId={battleRecord?.id || ''}
            result={battleResult as any}
            initialPlayerPawns={war.inventory.p}
            initialEnemyPawns={war.enemy_inventory.p}
            finalPlayerPawns={Math.max(0, war.inventory.p - battleResult.casualties.p - ((Object.values((battleResult as any).gains || {}) as number[]).reduce((a: number, b: number) => a + b, 0)))}
            finalEnemyPawns={Math.max(0, war.enemy_inventory.p - battleResult.enemy_casualties.p)}
            userSide={mySide}
          />
        )}
      </div>
    </div>
  );
};

export default Battle;
